import { getApiRoot } from '@/helpers/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import apiClient from '@plugins/api-client';
import { getKeycloak } from '@/helpers/keycloak';
import { useApi } from '@/composables/useApi';
import { useI18n } from 'vue-i18n';
import { useSnackbarStore } from '@/store/snackbar';

export const OfferTypes = {
  SolarPanels: 1,
  SolarBoilers: 2,
  HeatPumps: 3,
  HeatPumpBoiler: 4,
  Other: 5,
  Batteries: 6,
  ChargingStations: 10,
  Control: 15,
  Service: 25,
  EMS: 35,
  FloorHeating: 50,
  Repairs: 51,
};

export const OfferTypesIcons = {
  default: 'mdi-progress-wrench',
  solarPanels: '$energy',
  solarBoilers: 'mdi-sun-thermometer-outline',
  airToAirHeatPumps: 'mdi-heat-pump-outline',
  heatPumpBoiler: 'mdi-water-boiler',
  batteries: 'mdi-battery-outline',
  chargingStations: 'mdi-ev-station',
  floorHeating: 'mdi-heating-coil',
  ems: 'mdi-home-lightning-bolt-outline',
  repairs: 'mdi-wrench-outline',
};

export const useOfferStore = defineStore('offerStore', () => {
  const { t } = useI18n();
  const { apiGet, apiPost } = useApi();

  const snackbarStore = useSnackbarStore();

  const offer = ref(null);
  const versions = ref(null);

  const offerIsWithdrawn = computed(() => Boolean(offer.value?.withdrawnViaPortalTimestamp));
  const offerDocument = computed(() => getOfferDocument(offer.value));

  async function getOfferByID(id, versionNumber) {
    offer.value = await apiGet(`offers/${id}/versions/${versionNumber}`);
  }

  async function getOfferVersions(id) {
    const headers = { Authorization: `Bearer ${(await getKeycloak()).token}` };
    const response = await apiClient.get(
      `${getApiRoot()}/offers/${id}/versions`,
      { headers },
    );
    if (response.data.errors.length === 0) {
      versions.value = response.data.data;
    } else {
      throw new Error(response.data.errors);
    }
  }

  async function getOfferAttachments(id, versionNumber) {
    return apiGet(`offers/${id}/versions/${versionNumber}/attachments`);
  }

  function signOffer(offerId, versionId, base64image) {
    return apiPost(
      `offers/${offerId}/sign`,
      {
        version: versionId,
        file: base64image,
      },
    );
  }

  async function storeUpsell(type, name, file) {
    return await apiPost(`offers/${offer.value.offerId}/upsale`, {
      versionNumber: offer.value.versionNumber,
      type,
      name,
      file,
    });
  }

  async function submitCustomerView(offerId, versionNumber) {
    return await apiPost(`offers/${offerId}/seen`, { versionNumber });
  }

  async function submitWithdrawal() {
    if (!offer.value?.offerId) {
      return;
    }
    const result = await apiPost(`offers/${offer.value.offerId}/withdrawal`, { versionNumber: offer.value.versionNumber });

    if (!result[0]?.result) {
      snackbarStore.showError(t('genericServerError'));
    }

    await getOfferByID(offer.value.offerId, offer.value.versionNumber);
  }

  function getOfferDocument(offer) {
    return {
      name: 'offer',
      downloading: false,
      cdn: false,
      text: t('offerDetail.offer'),
      downloadName: `${t('offerDetail.offer')}-${offer.offerId}.pdf`,
      url: `offers/${offer.offerId}/files/${offer.offerTypeId}?type=print&versionNumber=${offer.versionNumber}`,
    };
  }

  return {
    offer,
    versions,
    offerDocument,
    offerIsWithdrawn,
    getOfferByID,
    getOfferVersions,
    getOfferAttachments,
    signOffer,
    storeUpsell,
    submitCustomerView,
    submitWithdrawal,
    getOfferDocument,
  };
});
